import { AGENT_ENTRIES, AGENT_EXAMPLES, AGENT_FILES, AGENTS_INTRO, AGENT_NOTE, AGENT_PARAMS, AGENT_RULES } from '@/content/agents';
import { Card } from '@/components/Card';
import { CopyField } from '@/components/CopyField';
import { Icon } from '@/components/Icon';
import { PageHero, Section, SectionHeading } from '@/components/Section';
import { SpecTableView } from '@/components/SpecTableView';
import { Seo } from '@/components/Seo';

const FILE_TABLE = {
  head: ['地址', '内容'],
  rows: AGENT_FILES.map((file) => [file.path, file.note]),
} as const;

const PARAM_TABLE = {
  head: ['参数', '是否必填', '说明'],
  rows: AGENT_PARAMS.map((param) => [param.name, param.required, param.note]),
} as const;

/** 「Agent 接入」：两条通道（阅读 / 检索）的说明与地址。内容见 content/agents.ts。 */
export function AgentsPage() {
  return (
    <>
      <Seo
        path="/agents"
        crumbs={[
          { name: '首页', path: '/' },
          { name: 'Agent 接入', path: '/agents' },
        ]}
      />

      <PageHero eyebrow={AGENTS_INTRO.eyebrow} title={AGENTS_INTRO.title} lead={AGENTS_INTRO.lead} />

      <Section>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-5 md:grid-cols-2">
          {AGENT_ENTRIES.map((entry) => (
            <Card key={entry.id} className="flex min-w-0 flex-col gap-4">
              <div className="flex items-center gap-3">
                <Icon name={entry.icon} size={18} className="text-fg-muted" />
                <span className="label-mono text-brand">{entry.label}</span>
              </div>
              <h2 className="text-lg font-semibold text-fg">{entry.title}</h2>
              <p className="text-sm leading-relaxed text-fg-muted">{entry.summary}</p>
              <ul className="flex flex-col gap-2 text-sm leading-relaxed text-fg-muted">
                {entry.points.map((point) => (
                  <li key={point} className="flex gap-2.5">
                    <Icon name="check" size={15} className="mt-0.5 shrink-0 text-brand" />
                    <span className="min-w-0">{point}</span>
                  </li>
                ))}
              </ul>
              <CopyField label={entry.addressLabel} value={entry.address} href={entry.address} className="mt-auto" />
            </Card>
          ))}
        </div>
      </Section>

      <Section tone="raised">
        <SectionHeading
          eyebrow="机器可读地址"
          title="五个固定入口"
          lead="除逐页 Markdown 随页面数量变化外，其余四个地址恒定，可作为接入的起点。"
          className="mb-8"
        />
        <SpecTableView table={FILE_TABLE} />
      </Section>

      <Section>
        <div className="grid grid-cols-[minmax(0,1fr)] gap-10 lg:grid-cols-[minmax(0,1fr)_26rem] lg:items-start">
          <div>
            <SectionHeading eyebrow="检索接口" title="一次请求定位条目" className="mb-6" />
            <SpecTableView table={PARAM_TABLE} firstColumnMono />
            <p className="mt-4 text-sm leading-relaxed text-fg-muted">
              返回体为 <code className="metric-value">ok / data</code> 结构，<code className="metric-value">data.results</code>{' '}
              中每条包含 <code className="metric-value">title</code>、<code className="metric-value">description</code>、
              <code className="metric-value">url</code> 与 <code className="metric-value">markdownUrl</code>。缺少{' '}
              <code className="metric-value">q</code> 返回 400，非 GET 方法返回 405。
            </p>
          </div>

          <div className="flex flex-col gap-4">
            {AGENT_EXAMPLES.map((example) => (
              <Card key={example.title} className="flex min-w-0 flex-col gap-3">
                <span className="label-mono">{example.title}</span>
                <pre className="overflow-x-auto rounded-lg border border-line bg-surface-2 px-3.5 py-3 text-xs leading-relaxed">
                  <code>{example.code}</code>
                </pre>
                <CopyField value={example.code} />
              </Card>
            ))}
          </div>
        </div>
      </Section>

      <Section tone="raised">
        <SectionHeading eyebrow="使用约定" title="抓取与引用" className="mb-6" />
        <ul className="flex max-w-3xl flex-col gap-3 text-sm leading-relaxed text-fg-muted">
          {AGENT_RULES.map((rule) => (
            <li key={rule} className="flex gap-2.5">
              <Icon name="check" size={15} className="mt-0.5 shrink-0 text-brand" />
              <span className="min-w-0">{rule}</span>
            </li>
          ))}
        </ul>
        <p className="mt-8 max-w-3xl border-t border-line pt-6 text-xs leading-relaxed text-fg-subtle">{AGENT_NOTE}</p>
      </Section>
    </>
  );
}
